import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { recruitmentService } from '../services/schools/recruitmentService';
import {
  DEFAULT_RECRUITMENT_CATALOG_SETTINGS,
  recruitmentCatalogSettingsService,
} from '../services/schools/recruitmentCatalogSettingsService';
import type { BrowseSchool, OutreachEntry, SavedSchool } from '../types/recruitment';

export const recruitmentQueryKeys = {
  allSchools: ['all-schools'] as const,
  savedSchoolIds: (userId: string) => ['saved-school-ids', userId] as const,
  savedSchools: (userId: string) => ['saved-schools', userId] as const,
  catalogSettings: ['recruitment-catalog-settings'] as const,
};

export const rowerRecruitmentProgramSearchParam = 'program';

export function refetchRowerSavedSchoolQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  userId: string
) {
  return Promise.all([
    queryClient.refetchQueries({ queryKey: recruitmentQueryKeys.savedSchoolIds(userId) }),
    queryClient.refetchQueries({ queryKey: recruitmentQueryKeys.savedSchools(userId) }),
  ]);
}

async function applyOptimisticSavedSchoolUpdate(
  queryClient: ReturnType<typeof useQueryClient>,
  userId: string | undefined,
  schoolId: string,
  patch: Partial<SavedSchool>
) {
  if (!userId) return undefined;
  const schoolsKey = recruitmentQueryKeys.savedSchools(userId);
  await queryClient.cancelQueries({ queryKey: schoolsKey });
  const previousSchools = queryClient.getQueryData<SavedSchool[]>(schoolsKey);
  queryClient.setQueryData<SavedSchool[]>(schoolsKey, (current) =>
    current?.map((school) => (school.id === schoolId ? { ...school, ...patch } : school))
  );
  return { previousSchools };
}

function restoreSavedSchools(
  queryClient: ReturnType<typeof useQueryClient>,
  userId: string | undefined,
  previousSchools: SavedSchool[] | undefined
) {
  if (userId && previousSchools !== undefined) {
    queryClient.setQueryData(recruitmentQueryKeys.savedSchools(userId), previousSchools);
  }
}

export const useAllSchools = () =>
  useQuery({
    queryKey: recruitmentQueryKeys.allSchools,
    queryFn: () => recruitmentService.getAllSchools(),
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

export const useRecruitmentCatalogSettings = () =>
  useQuery({
    queryKey: recruitmentQueryKeys.catalogSettings,
    queryFn: () => recruitmentCatalogSettingsService.getSettings(),
    staleTime: 5 * 60 * 1000,
    placeholderData: DEFAULT_RECRUITMENT_CATALOG_SETTINGS,
  });

export const useSavedSchoolIds = (userId: string | undefined) =>
  useQuery({
    queryKey: recruitmentQueryKeys.savedSchoolIds(userId!),
    queryFn: () => recruitmentService.getSavedSchoolIds(userId!),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

export const useSavedSchools = (userId: string | undefined) =>
  useQuery({
    queryKey: recruitmentQueryKeys.savedSchools(userId!),
    queryFn: () => recruitmentService.getSavedSchools(userId!),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

export const useAddSchool = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, schoolId }: { userId: string; schoolId: string }) =>
      recruitmentService.addSchool(userId, schoolId),
    onMutate: async ({ userId, schoolId }) => {
      const idsKey = recruitmentQueryKeys.savedSchoolIds(userId);
      const schoolsKey = recruitmentQueryKeys.savedSchools(userId);
      await queryClient.cancelQueries({ queryKey: idsKey });
      await queryClient.cancelQueries({ queryKey: schoolsKey });

      const previousIds = queryClient.getQueryData<string[]>(idsKey);
      queryClient.setQueryData<string[]>(idsKey, (current = []) =>
        current.includes(schoolId) ? current : [...current, schoolId]
      );

      const previousSchools = queryClient.getQueryData<SavedSchool[]>(schoolsKey);
      const catalog = queryClient.getQueryData<BrowseSchool[]>(recruitmentQueryKeys.allSchools);
      const school = catalog?.find((entry) => entry.id === schoolId);
      if (school) {
        queryClient.setQueryData<SavedSchool[]>(schoolsKey, (current = []) => {
          if (current.some((entry) => entry.id === schoolId)) return current;
          return [
            ...current,
            {
              ...school,
              notes: '',
              favorite: false,
              academic_status: -1,
              recruitment_status: -1,
              outreach_entries: [],
            },
          ];
        });
      }

      return { previousIds, previousSchools, userId };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousIds !== undefined) {
        queryClient.setQueryData(
          recruitmentQueryKeys.savedSchoolIds(context.userId),
          context.previousIds
        );
      }
      restoreSavedSchools(queryClient, context?.userId, context?.previousSchools);
    },
    onSettled: (_data, _error, variables) => {
      void refetchRowerSavedSchoolQueries(queryClient, variables.userId);
    },
  });
};

export const useRemoveSchool = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, schoolId }: { userId: string; schoolId: string }) =>
      recruitmentService.removeSchool(userId, schoolId),
    onSettled: (_data, _error, variables) => {
      void refetchRowerSavedSchoolQueries(queryClient, variables.userId);
    },
  });
};

export const useToggleFavorite = (userId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ schoolId, favorite }: { schoolId: string; favorite: boolean }) =>
      recruitmentService.toggleFavorite(userId!, schoolId, favorite),
    onMutate: async ({ schoolId, favorite }) =>
      applyOptimisticSavedSchoolUpdate(queryClient, userId, schoolId, { favorite }),
    onError: (_error, _variables, context) => {
      restoreSavedSchools(queryClient, userId, context?.previousSchools);
    },
    onSettled: () => {
      if (userId) void refetchRowerSavedSchoolQueries(queryClient, userId);
    },
  });
};

export const useSaveNotes = (userId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ schoolId, notes }: { schoolId: string; notes: string }) =>
      recruitmentService.saveNotes(userId!, schoolId, notes),
    onSettled: () => {
      if (userId) void refetchRowerSavedSchoolQueries(queryClient, userId);
    },
  });
};

export const useSaveOutreach = (userId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ schoolId, entries }: { schoolId: string; entries: OutreachEntry[] }) =>
      recruitmentService.saveOutreach(userId!, schoolId, entries),
    onMutate: async ({ schoolId, entries }) =>
      applyOptimisticSavedSchoolUpdate(queryClient, userId, schoolId, {
        outreach_entries: entries,
      }),
    onError: (_error, _variables, context) => {
      restoreSavedSchools(queryClient, userId, context?.previousSchools);
    },
    onSettled: () => {
      if (userId) void refetchRowerSavedSchoolQueries(queryClient, userId);
    },
  });
};

export const useUpdateProgress = (userId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      schoolId,
      type,
      step,
    }: {
      schoolId: string;
      type: 'academic' | 'recruitment';
      step: number;
    }) => recruitmentService.updateProgress(userId!, schoolId, type, step),
    onMutate: async ({ schoolId, type, step }) =>
      applyOptimisticSavedSchoolUpdate(
        queryClient,
        userId,
        schoolId,
        type === 'academic' ? { academic_status: step } : { recruitment_status: step }
      ),
    onError: (_error, _variables, context) => {
      restoreSavedSchools(queryClient, userId, context?.previousSchools);
    },
    onSettled: () => {
      if (userId) void refetchRowerSavedSchoolQueries(queryClient, userId);
    },
  });
};
